import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useRef, useState } from 'react';
import type { RiskAssessmentWell } from '@/types';
import { ocenaRyzyka as riskApi } from '@/api/endpoints';
import { validateUploadSize } from '@/lib/uploadLimits';
import { Icon } from '@/components/Icon';

/**
 * Georeferencer mapy analizy ryzyka: obraz mapy → punkty kontrolne (studnie o znanych współrzędnych
 * wskazane na obrazie) → transformacja afiniczna piksel→WGS84 → obrys obszaru zasilania → poligon WGS84.
 *
 * UCZCIWIE o ograniczeniach: to jest georeferencja przez punkty kontrolne, jak w narzędziu QGIS.
 * Wynik zależy od dokładności wskazania studni i jest zawsze PROWIZORYCZNY — wymaga oceny błędu
 * dopasowania (RMS) i akceptacji człowieka. Bez ≥3 rozstawionych studni na mapie transformacja nie
 * powstanie. Obrys rysuje człowiek na obrazie (asysta, nie autorytatywna wektoryzacja rastra).
 */
type Pt = { x: number; y: number };
type Gcp = { x: number; y: number; wellId: number };

export function GeoreferencerModal({ raId, wells, onSaved, onClose }: {
  raId: number;
  wells: RiskAssessmentWell[];
  onSaved: (geoJson: string) => void;
  onClose: () => void;
}) {
  const [img, setImg] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dim, setDim] = useState<{ w: number; h: number } | null>(null);
  const [mode, setMode] = useState<'gcp' | 'trace'>('gcp');
  const [gcps, setGcps] = useState<Gcp[]>([]);
  const [poly, setPoly] = useState<Pt[]>([]);
  const [wellForGcp, setWellForGcp] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSzkic, setAiSzkic] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const zeWsp = wells.filter(w => w.latitude != null && w.longitude != null);
  const uzyteId = new Set(gcps.map(g => g.wellId));
  const wolne = zeWsp.filter(w => !uzyteId.has(w.id));

  const wczytaj = (f: File) => {
    const problem = validateUploadSize(f);
    if (problem) { setErr(problem); return; }
    const rd = new FileReader();
    rd.onload = () => {
      const url = String(rd.result);
      const im = new Image();
      im.onload = () => setDim({ w: im.naturalWidth, h: im.naturalHeight });
      im.src = url;
      setImg(url); setFile(f); setGcps([]); setPoly([]); setErr(null); setAiSzkic(false);
    };
    rd.readAsDataURL(f);
  };

  /** AI-vision: propozycja konturu jako punkty znormalizowane [0..1] → piksele obrazu. SZKIC do korekty. */
  const zaproponujKontur = () => {
    if (!file || !dim) return;
    setAiBusy(true); setErr(null);
    riskApi.proposeOutline(raId, file)
      .then(r => {
        const pts = (r.points ?? []).filter(p => Array.isArray(p) && p.length >= 2)
          .map(([nx, ny]) => ({ x: nx * dim.w, y: ny * dim.h }));
        if (pts.length < 3) { setErr('Nie rozpoznano obszaru na mapie — obrysuj ręcznie.'); return; }
        setPoly(pts); setAiSzkic(true);
      })
      .catch(e => setErr(e?.message ?? 'Propozycja konturu nie powiodła się.'))
      .finally(() => setAiBusy(false));
  };

  /** Współrzędne kliknięcia w PIKSELACH oryginału obrazu (niezależne od skali wyświetlania). */
  const punktZKlikniecia = (e: React.MouseEvent): Pt | null => {
    const el = imgRef.current; if (!el || !dim) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return null;   // zdegenerowany layout — nie dodawaj wadliwego punktu
    const x = ((e.clientX - r.left) / r.width) * dim.w;
    const y = ((e.clientY - r.top) / r.height) * dim.h;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  };

  const klik = (e: React.MouseEvent) => {
    const p = punktZKlikniecia(e); if (!p) return;
    if (mode === 'gcp') {
      if (!wellForGcp) { setErr('Najpierw wybierz studnię, którą wskazujesz na mapie.'); return; }
      setGcps(g => [...g, { x: p.x, y: p.y, wellId: Number(wellForGcp) }]);
      setWellForGcp(''); setErr(null);
    } else {
      setPoly(pl => [...pl, p]);
    }
  };

  /**
   * Afiniczna transformacja piksel→(lng,lat) metodą najmniejszych kwadratów z punktów kontrolnych.
   * lng = a·x + b·y + c ; lat = d·x + e·y + f. Zwraca też błąd RMS dopasowania [m ~ stopnie].
   */
  const dopasowanie = (): { fn: (p: Pt) => [number, number]; rms: number } | null => {
    if (gcps.length < 3) return null;
    const byId = new Map(zeWsp.map(w => [w.id, w]));
    const rows = gcps.map(g => {
      const w = byId.get(g.wellId)!;
      return { x: g.x, y: g.y, lng: Number(w.longitude), lat: Number(w.latitude) };
    });
    // normalne równania dla [a,b,c]: (XᵀX)·β = Xᵀ·v, X = [x y 1]
    let Sxx = 0, Sxy = 0, Sx = 0, Syy = 0, Sy = 0, S1 = 0;
    let SxLng = 0, SyLng = 0, S1Lng = 0, SxLat = 0, SyLat = 0, S1Lat = 0;
    for (const r of rows) {
      Sxx += r.x * r.x; Sxy += r.x * r.y; Sx += r.x;
      Syy += r.y * r.y; Sy += r.y; S1 += 1;
      SxLng += r.x * r.lng; SyLng += r.y * r.lng; S1Lng += r.lng;
      SxLat += r.x * r.lat; SyLat += r.y * r.lat; S1Lat += r.lat;
    }
    const M: number[][] = [[Sxx, Sxy, Sx], [Sxy, Syy, Sy], [Sx, Sy, S1]];
    const inv = invert3(M); if (!inv) return null;
    const solve = (b: [number, number, number]) => [
      inv[0][0] * b[0] + inv[0][1] * b[1] + inv[0][2] * b[2],
      inv[1][0] * b[0] + inv[1][1] * b[1] + inv[1][2] * b[2],
      inv[2][0] * b[0] + inv[2][1] * b[1] + inv[2][2] * b[2],
    ] as [number, number, number];
    const [a, b, c] = solve([SxLng, SyLng, S1Lng]);
    const [d, e, f] = solve([SxLat, SyLat, S1Lat]);
    const fn = (p: Pt): [number, number] => [a * p.x + b * p.y + c, d * p.x + e * p.y + f];
    // RMS residuum w metrach (przybliżenie: 1°≈111 km, długość skalowana cos(lat))
    let sq = 0;
    for (const r of rows) {
      const [lng, lat] = fn({ x: r.x, y: r.y });
      const dLat = (lat - r.lat) * 111000;
      const dLng = (lng - r.lng) * 111000 * Math.cos((r.lat * Math.PI) / 180);
      sq += dLat * dLat + dLng * dLng;
    }
    return { fn, rms: Math.sqrt(sq / rows.length) };
  };

  const fit = dopasowanie();

  const dobreDopasowanie = !!fit && Number.isFinite(fit.rms);

  const zapisz = () => {
    if (!dobreDopasowanie) { setErr('Georeferencja niepoprawna — wskaż 3 dobrze rozstawione (nie w jednej linii) studnie.'); return; }
    if (poly.length < 3) { setErr('Obrysuj obszar zasilania (min. 3 punkty).'); return; }
    const ring = poly.map(p => fit!.fn(p));
    if (ring.some(([lng, lat]) => !Number.isFinite(lng) || !Number.isFinite(lat)
        || lng < -180 || lng > 180 || lat < -90 || lat > 90)) {
      setErr('Przeliczone współrzędne obszaru poza zakresem WGS84 — sprawdź punkty kontrolne.');
      return;
    }
    ring.push(ring[0]); // domknij pierścień
    const geo = { type: 'Polygon', coordinates: [ring] };
    // zapisz też OBRAZ mapy jako proweniencję obszaru (best-effort — nie blokuje zapisu poligonu)
    if (file) riskApi.saveCatchmentMap(raId, file).catch(() => {});
    onSaved(JSON.stringify(geo));
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { file, gcps, poly }, onClose, busy: aiBusy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-[900px] max-h-[94vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100 flex items-center gap-2">
          <Icon name="map" className="w-4 h-4 text-ink-400" />
          <h2 className="text-[15px] font-semibold text-ink-900">Obszar zasilania z mapy analizy ryzyka</h2>
          <button onClick={requestClose} className="btn btn-ghost btn-sm ml-auto">✕</button>
        </div>

        <div className="p-5 grid gap-3">
          <div className="text-[12px] text-ink-500">
            Wgraj obraz mapy z analizy ryzyka, wskaż na nim ≥3 studnie o znanych współrzędnych (punkty
            kontrolne), a potem obrysuj obszar zasilania. Program przeliczy obrys na współrzędne WGS84.
            Wynik jest prowizoryczny — sprawdź błąd dopasowania i skoryguj obrys.
          </div>

          {!img && (
            <label className="btn btn-secondary btn-sm self-start cursor-pointer">
              <Icon name="upload" className="w-3.5 h-3.5" /> Wgraj obraz mapy
              <input type="file" accept="image/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) wczytaj(f); e.currentTarget.value = ''; }} />
            </label>
          )}

          {img && zeWsp.length < 3 && (
            <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">
              Georeferencja wymaga ≥3 studni ze współrzędnymi. Uzupełnij studnie ujęcia (panel „Studnie ujęcia").
            </div>
          )}

          {img && zeWsp.length >= 3 && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <button onClick={() => setMode('gcp')} className={'btn btn-sm ' + (mode === 'gcp' ? 'btn-primary' : 'btn-ghost')}>1. Punkty kontrolne ({gcps.length})</button>
                  <button onClick={() => setMode('trace')} disabled={gcps.length < 3} className={'btn btn-sm ' + (mode === 'trace' ? 'btn-primary' : 'btn-ghost')}>2. Obrys obszaru ({poly.length})</button>
                </div>
                {mode === 'gcp' && (
                  <select value={wellForGcp} onChange={e => setWellForGcp(e.target.value)} className="input btn-sm max-w-[220px]">
                    <option value="">— wybierz studnię do wskazania —</option>
                    {wolne.map(w => <option key={w.id} value={w.id}>{w.designation} ({Number(w.latitude).toFixed(4)}, {Number(w.longitude).toFixed(4)})</option>)}
                  </select>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {mode === 'trace' && fit && (
                    <button onClick={zaproponujKontur} disabled={aiBusy} className="btn btn-ghost btn-sm" title="Proponuje kontur na podstawie mapy — szkic do korekty">
                      <Icon name="sparkles" className="w-3.5 h-3.5" /> {aiBusy ? 'Generuję…' : 'Zaproponuj kontur'}
                    </button>
                  )}
                  {mode === 'gcp'
                    ? <button onClick={() => setGcps(g => g.slice(0, -1))} disabled={!gcps.length} className="btn btn-ghost btn-sm">Cofnij punkt</button>
                    : <button onClick={() => { setPoly(p => p.slice(0, -1)); setAiSzkic(false); }} disabled={!poly.length} className="btn btn-ghost btn-sm">Cofnij wierzchołek</button>}
                </div>
              </div>

              <div className="relative inline-block border border-ink-200 rounded overflow-hidden self-start max-w-full">
                <img ref={imgRef} src={img} alt="mapa analizy ryzyka" onClick={klik}
                  className="block max-w-full max-h-[62vh] cursor-crosshair select-none" draggable={false} />
                {dim && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox={`0 0 ${dim.w} ${dim.h}`} preserveAspectRatio="none">
                    {poly.length > 0 && (
                      <polygon points={poly.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="rgba(14,116,144,0.18)" stroke="#0e7490" strokeWidth={Math.max(1, dim.w / 400)} />
                    )}
                    {gcps.map((g, i) => (
                      <g key={i}>
                        <circle cx={g.x} cy={g.y} r={Math.max(3, dim.w / 180)} fill="#dc2626" stroke="#fff" strokeWidth={Math.max(1, dim.w / 500)} />
                        <text x={g.x + dim.w / 120} y={g.y} fontSize={dim.w / 55} fill="#dc2626">
                          {zeWsp.find(w => w.id === g.wellId)?.designation ?? ''}
                        </text>
                      </g>
                    ))}
                  </svg>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap text-[12px]">
                {!fit
                  ? <span className="text-ink-400">Wskaż jeszcze {Math.max(0, 3 - gcps.length)} punkt(y) kontrolne.</span>
                  : !Number.isFinite(fit.rms)
                    ? <span className="text-red-600">Punkty kontrolne w jednej linii lub pokrywają się — rozstaw je szerzej.</span>
                    : <span className={fit.rms > 50 ? 'text-amber-600' : 'text-green-600'}>
                        Błąd dopasowania (RMS): {fit.rms.toFixed(0)} m {fit.rms > 50 ? '— duży, dokładniej wskaż studnie' : '— dobre'}
                      </span>}
              </div>

              {aiSzkic && poly.length >= 3 && (
                <div className="text-[12px] text-amber-600">Kontur zaproponowany przez AI — to SZKIC, popraw wierzchołki przed zapisem.</div>
              )}
              {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
              <div className="flex justify-end gap-2">
                <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
                <button onClick={zapisz} disabled={!dobreDopasowanie || poly.length < 3} className="btn btn-primary btn-sm">
                  <Icon name="check" className="w-3.5 h-3.5" /> Zapisz jako obszar zasilania
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Odwrotność macierzy 3×3 (reguła Cramera); null gdy osobliwa (współliniowe punkty kontrolne). */
function invert3(m: number[][]): number[][] | null {
  const [a, b, c] = m[0], [d, e, f] = m[1], [g, h, i] = m[2];
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-12) return null;
  const D = -(b * i - c * h), E = a * i - c * g, F = -(a * h - b * g);
  const G = b * f - c * e, H = -(a * f - c * d), I = a * e - b * d;
  return [[A / det, D / det, G / det], [B / det, E / det, H / det], [C / det, F / det, I / det]];
}
